import { CallStatus } from '@hooks/useCallStatus';
import { TypedEmitter } from 'tiny-typed-emitter';
import { createLocalAudioTrack, createLocalVideoTrack, LocalAudioTrack, LocalVideoTrack, Room } from 'twilio-video';
import { queryClient } from './queryClient';
import ServiceApi from 'vl-common/src/services/service.api';
import assert from 'assert';

export const lobby = Symbol('Room extensions to support the Lobby paradigm');
export type RingerStopReason = 'timeout' | 'declined' | 'cancelled' | '';

if (typeof window !== 'undefined') {
  // @ts-ignore
  window['lobby'] = lobby;
}

export function isTestCall(roomName: string) {
  return roomName.startsWith('test-');
}

interface MessageEvents {
  callStatusChanged: (newStatus: CallStatus) => void;
  ringerStopReason: (reason: RingerStopReason) => void;
}

export class Lobby extends TypedEmitter<MessageEvents> {
  private microphoneDeviceId?: string;
  private cameraDeviceId?: string;
  private localMedia?: [LocalAudioTrack, LocalVideoTrack];
  private _lastRingerStopReason: RingerStopReason = '';

  get lastRingerStopReason() {
    return this._lastRingerStopReason;
  }

  set lastRingerStopReason(reason: RingerStopReason) {
    if (this._lastRingerStopReason === reason) return;
    this._lastRingerStopReason = reason;
    this.emit('ringerStopReason', reason);
  }

  get callStatus() {
    return this._callStatus;
  }

  set callStatus(newStatus: CallStatus) {
    if (this._callStatus === newStatus) return;
    this._callStatus = newStatus;
    this.emit('callStatusChanged', newStatus);
  }

  private static lobbiesByRoom = new WeakMap<Room, Lobby>();

  public static forRoom(room: Room) {
    if (!Lobby.lobbiesByRoom.has(room)) {
      Lobby.lobbiesByRoom.set(room, new Lobby(room));
    }

    return Lobby.lobbiesByRoom.get(room)!;
  }

  public static clearAllLobbies() {
    Lobby.lobbiesByRoom = new WeakMap<Room, Lobby>();
  }

  constructor(
    public readonly room: Room,
    private _callStatus: CallStatus = 'not-started'
  ) {
    super();
    const appointment_id = room.name.split('-').at(-1);

    // when the remote fires up their A/V
    room.on('trackSubscribed', (track) => {
      if (track.kind === 'data') return;
      this.callStatus = 'in-progress';
    });

    // if this room corresponds to a real call then the call status is stored in the backend so that active calls can
    // be re-started
    if (!isTestCall(room.name)) {
      // derive the current callStatus
      queryClient
        .fetchQuery({
          queryKey: ['videoCallStatus', Number(appointment_id)],
          queryFn: () => ServiceApi.get(`/booking/appointment/status/${ServiceApi.userGuid}/${appointment_id}`)
        })
        .then((status) => {
          if (status?.data?.appointment_stati.length > 0) {
            const { clin_start, clin_end } = status.data.appointment_stati[0];

            if (this.callStatus === 'not-started') {
              function getCallStatus() {
                if (clin_end) return 'completed';
                if (clin_start) return 'in-progress';
                return 'not-started';
              }

              this.callStatus = getCallStatus();
            }
          }
        });
    }
  }

  async join() {
    if (!this.localMedia) {
      this.localMedia = await Promise.all([
        createLocalAudioTrack({ deviceId: this.microphoneDeviceId }),
        createLocalVideoTrack({ deviceId: this.cameraDeviceId })
      ]);
      this.room.localParticipant.on('trackPublicationFailed', (error) => {
        console.log('Track publication failed: ', error.message);
      });
    }

    assert(this.localMedia);

    this.localMedia.length = 2;

    if (!this.localMedia[0] || this.localMedia[0].isStopped) {
      this.localMedia[0] = await createLocalAudioTrack({ deviceId: this.microphoneDeviceId });
    }
    if (!this.localMedia[1] || this.localMedia[1].isStopped) {
      this.localMedia[1] = await createLocalVideoTrack({ deviceId: this.cameraDeviceId });
    }

    return this.room.localParticipant.publishTracks(this.localMedia);
  }

  async leave({ andDisconnect } = { andDisconnect: true }) {
    if (!this.localMedia) return this.room;

    [...this.room.localParticipant.videoTracks.values(), ...this.room.localParticipant.audioTracks.values()].forEach(
      (publication) => {
        publication.track.stop();
      }
    );
    this.room.localParticipant.unpublishTracks(this.localMedia);
    this.localMedia = undefined;

    if (!andDisconnect) return this.room;

    return this.room.disconnect();
  }

  async setMicrophoneDeviceId(deviceId: string) {
    this.microphoneDeviceId = deviceId;

    if (this.localMedia) {
      return this.localMedia[0].restart({ deviceId });
    }

    return Promise.resolve();
  }

  async setCameraDeviceId(deviceId: string) {
    this.cameraDeviceId = deviceId;

    if (this.localMedia) {
      return this.localMedia[1].restart({ deviceId });
    }

    return Promise.resolve();
  }
}

if (typeof window !== 'undefined') {
  // @ts-ignore
  window.Lobby = Lobby;
}
